import { filterHelpers } from 'global/common/filtering';
import { sortObject } from 'global/common/sorting';
import { gEnums } from 'global/enums/globalEnums';
import _ from 'lodash';
import { useCallback, useContext, useMemo, useRef } from 'react';
import { FrameworkContext } from '../FrameworkContent';
import { ParentContext } from '../ParentContext';
import { useAppUserAccess } from './useAppUserAccess';
import { SportsDistrictContext } from 'projectSpecific/sports/cnr/contexts/SportsDistrictContext';
import { _showAppLogs } from '../LogContext';

export const usePageArea = () => {

  const _apits = gEnums.additionalPageItemTypes

  const appUserAccess = useAppUserAccess()
  const { accessLevel } = appUserAccess ?? {}

  const parentContexts = useContext(ParentContext)
  const { states, fns } = parentContexts ?? {}
  const { paps_state, page_state, currentSettings_state } = states ?? {}

  const { appUser_fns } = fns

  const frameworkContext = useContext(FrameworkContext);
  const { framework_state } = frameworkContext ?? {}

  const sportsDistrictContext = useContext(SportsDistrictContext)
  const { sportsDistrict_state } = sportsDistrictContext ?? {}

  const infoRef = useRef({
    currentSettings_state,
    framework_state,
    page_state,
    paps_state,
    sportsDistrict_state,
  })

  // Update the ref on every render
  infoRef.current = {
    currentSettings_state,
    framework_state,
    page_state,
    paps_state,
    sportsDistrict_state,
  }

  const getPageItems = useCallback(() => {

    const {
      currentSettings_state,
      framework_state,
      page_state,
      paps_state,
      sportsDistrict_state,
    } = infoRef.current

    const { pathName, pathViews } = paps_state ?? {}

    const { aps_global } = _.get(currentSettings_state, 'gvviSettings', {});
    const { appGallery, pageOptions, projectModules } = aps_global ?? {}
    const { allowGallery } = appGallery ?? {}

    const { aps_page } = _.get(page_state, 'pageSettings', {});
    const { viewItems: viewItems_page } = aps_page ?? {}

    const { desktopMode } = framework_state ?? {}

    const { district_data } = sportsDistrict_state ?? {}
    const { rssFeeders } = district_data ?? {}

    const pageItemsActive = getPageItemsActive({
      viewItems_page,
      desktopMode,
      accessLevel,
      appUser_fns
    })

    if (pageItemsActive) {
      const _vips = addOtherPageItems({
        _apits,
        pageItemsActive,
        projectModules,
        pageOptions,
        aps_page,
        allowGallery,
        rssFeeders,
        pathViews
      });
      if (_showAppLogs.pageInfo) {
        console.group('🛡️ getPageItems', pathName)
        console.log('aps_page', aps_page)
        console.log('pageItemsActive', _vips)
        console.groupEnd()
      }
      return { pageItemsActive: _vips };
    }
    return { pageItemsActive };
  }, [])

  return { getPageItems };

}

const getPageItemsActive = ({ viewItems_page, desktopMode, accessLevel, appUser_fns }) => {

  // get only viewItems marked with show = true
  const showProp = desktopMode ? gEnums.showTypes.showDesktop : gEnums.showTypes.show

  // list of pageItems with their props filter to shown only

  let pageItems_adjusted = filterHelpers.filterObject(viewItems_page, showProp)

  appUser_fns.validateAccess_items(pageItems_adjusted)

  let splitItems;

  if (pageItems_adjusted) {
    Object.keys(pageItems_adjusted).forEach((key, index) => {
      const pageItemAdjusted = pageItems_adjusted[key]
      const { combine, adminOnly } = pageItemAdjusted ?? {}

      if (index === 0) {
        pageItemAdjusted._isPrimary = true
      }

      if (adminOnly) {
        if (!accessLevel || accessLevel < gEnums.accessLevels.admin.value) {
          delete pageItems_adjusted[key]
        }
      }

      const { useCombine, combineWith } = combine ?? {}

      // remove combined pageItems
      if (useCombine && combineWith && combineWith.length > 0) {
        pageItemAdjusted.combinedGroups = {}
        combineWith.forEach(key => {
          pageItemAdjusted.combinedGroups[key] = pageItems_adjusted[key]
          delete pageItems_adjusted[key]
        })
      }
    })
    pageItems_adjusted = sortObject(pageItems_adjusted, 'position')
  }

  if (splitItems) {
    Object.keys(splitItems).forEach(k => {
      pageItems_adjusted[k] = splitItems[k]
    })
  }

  return pageItems_adjusted
}

const addOtherPageItems = ({ _apits, pageItemsActive, projectModules, pageOptions, aps_page, allowGallery, rssFeeders, pathViews }) => {

  if (!pageItemsActive) return {};

  const _dts = gEnums.displayTypes

  const _pia = { ...pageItemsActive };

  const { allowQuestionsAndAnswers, allowVoting } = projectModules ?? {};
  const { allowGeoList, allowGeoLocation, allowFeeds, allowImageLinks, allowTwitter, allowVideoLinks } = pageOptions ?? {};
  const { feeds: feeds_asp, geoList: geoList_asp, geoLocation: geoLocation_asp, imageLinks: imageLinks_asp, questionsAndAnswers: questionsAndAnswers_asp, twitter: twitter_asp, videoLinks: videoLinks_asp, voting: voting_asp } = aps_page ?? {}

  const count = Object.keys(pageItemsActive).length;

  // allowQuestionsAndAnswers_g && showOnPage_qAndA && _questionsAndAnsweFrs?.questionActive
  if (allowQuestionsAndAnswers) {
    _pia[_apits.questionsAndAnswers] = addOtherPageItem(_dts.questionsAndAnswers, questionsAndAnswers_asp, count, 'Q and A');
  }
  if (allowVoting && voting_asp?.allowVoting) {
    _pia[_apits.voting] = addOtherPageItem(_dts.questionsAndAnswers, questionsAndAnswers_asp, count, 'Q and A');
  }
  if (allowFeeds && feeds_asp?.showFeed) {
    // feedPageItemPosition || count
    _pia[_apits.feeds] = addOtherPageItem(_dts.feeds, feeds_asp, count, 'Media');
  }
  if (allowGeoList && geoList_asp?.showGeoList) {
    _pia[_apits.geoList] = addOtherPageItem(_dts.geoList, imageLinks_asp, count, geoList_asp.geoListCaption || null);
  }
  if (allowGeoLocation && geoLocation_asp?.showGeoLocation) {
    _pia[_apits.geoLocation] = addOtherPageItem(_dts.geoLocation, imageLinks_asp, count);
  }
  if (allowImageLinks && imageLinks_asp?.showImageLinks) {
    _pia[_apits.imageLinks] = addOtherPageItem(_dts.imageLinks, imageLinks_asp, count);
  }
  if (allowTwitter && twitter_asp?.showTwitter) {
    _pia[_apits.twitter] = addOtherPageItem(_dts.twitter, twitter_asp, count);
  }
  if (allowVideoLinks && videoLinks_asp?.showVideoLinks) {
    _pia[_apits.videoLinks] = addOtherPageItem(_dts.videoLinks, videoLinks_asp, count);
  }

  if (!_.isEmpty(rssFeeders) && pathViews.districts) {
    _pia[_apits.feeds] = addOtherPageItem(_dts.feeds, videoLinks_asp, 3, 'Media');
  }

  if (allowGallery && galleryPages?.includes(view) && viewKey) {
    _pia[_apits.photos] = addOtherPageItem(_dts.gallery, appGallery, count);
  }

  return _pia;
};

const addOtherPageItem = (displayType, componentProps, existingCount, caption) => {
  return {
    key: displayType,
    position: existingCount ? existingCount : 2,
    show: true,
    display: {
      caption: caption ? caption : _.startCase(displayType),
      displayType: displayType,
    },
    _viewPermissionType: gEnums.viewPermissionTypes.allow,
    componentProps
  }
}